import { Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';
import { ObjectUtil } from '../../../../../components/common/object-util/object-util';

/**
 * ValuationSummary Widget
 */
@Component({
  selector: 'valuation-summary-widget',
  templateUrl: './valuation-summary-widget.component.html',
  styleUrls: ['../../common/css/general.scss', '../../common/css/text-and-list.scss', './valuation-summary-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ValuationSummaryWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Should custom labels be disabled?
   */
  disableCustomLabels = true;

  /**
   * Should Weightings be shown?
   */
  get showWeightings() {
    const layout: string = this.widgetConfig.widgetSettings.currentLayout;
    return layout !== 'Short';
  }

  /**
   * Are Weightings invalid?
   */
  get weightingsInvalid() {
    const data = this.widgetData;
    const values = this.rows.filter(r => r.selected && r.fieldName !== null).map(r => data[r.fieldName] || 0);
    const sum = values.reduce((acc, value) => acc + parseFloat(value), 0);

    //assume valid if nothing entered or sum is not 100
    return !(sum === 0 || sum === 100);
  }

  /**
   * Delete current row subject
   */
  private deleteCurrentRowSubject = new Subject<void>();

  /**
   * Delete current row observable
   */
  public deleteCurrentRow$ = this.deleteCurrentRowSubject.asObservable();

  /**
   * Add row subject
   */
  private addRowSubject = new Subject<any>();

  /**
   * Add row observable
   */
  public addRow$ = this.addRowSubject.asObservable();

  /**
   * Move current row up subject
   */
  private moveCurrentRowUpSubject = new Subject<void>();

  /**
   * Move current row up observable
   */
  public moveCurrentRowUp$ = this.moveCurrentRowUpSubject.asObservable();

  /**
   * Move current row down subject
   */
  private moveCurrentRowDownSubject = new Subject<void>();

  /**
   * Move current row down observable
   */
  public moveCurrentRowDown$ = this.moveCurrentRowDownSubject.asObservable();

  /**
   * Text & List section rows
   */
  rows: any = [];

  /**
   * Text & List section name
   */
  readonly textAndListSectionName: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'valuation-summary', false);
    this.textAndListSectionName = `${this.name}`;
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("valuationSummary", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = false;
    }
    //upgrade for layout usage if required
    const settings = this.widgetConfig.widgetSettings;
    settings.currentLayout = settings.currentLayout || "Default";
    settings.availableLayouts = settings.availableLayouts || [
      "Default",
      "Short"
    ];

    this.loadReflist('ref_l3_valuation_summary_methodology', !this.disableCustomLabels);

    this.mockupData$ = this.widgetService.loadMockup('valuationSummary').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('valuationSummary', this.getReport()).pipe();
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    // percentages are in form of 0.13 for 13% => convert
    this.percentageToUI(data, "hardCoreTopSliceApproachWeighting");
    this.percentageToUI(data, "directCapitalisationApproachWeighting");
    this.percentageToUI(data, "discountedCashFlowApproachWeighting");

    this.percentageToUI(data, "costApproachWeighting");
    this.percentageToUI(data, "salesComparisonApproachWeighting");
    this.percentageToUI(data, "incomeCapitalizationApproachWeighting");

    //initialize Text&List section AFTER data is loaded.. to adjust data for backward compatibility
    setTimeout(() => {
      this.initializeSection();
    });

    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    const saveObject = ObjectUtil.deepCopy(this.widgetData); //Deep copy because we'd change the UI

    //clear weightings for deleted/not shown rows
    for (const row of this.rows.filter(r => !r.selected && r.fieldName !== null)) {
      saveObject[row.fieldName] = null;
    }

    // percentages are in form of 0.13 for 13% => convert
    this.percentageFromUI(saveObject, "hardCoreTopSliceApproachWeighting");
    this.percentageFromUI(saveObject, "directCapitalisationApproachWeighting");
    this.percentageFromUI(saveObject, "discountedCashFlowApproachWeighting");

    this.percentageFromUI(saveObject, "costApproachWeighting");
    this.percentageFromUI(saveObject, "salesComparisonApproachWeighting");
    this.percentageFromUI(saveObject, "incomeCapitalizationApproachWeighting");

    return saveObject;
  }

  /**
   * Initializes the Text & List section
   */
  private initializeSection() {

    let preselected = 1;
    const hasSection = !!this.widgetConfig.widgetSettings.textAndListConfigs;
    if (!hasSection && this.widgetData.capitalisationApproachId) {
      //preselect one that was chosen before widget switched to Text&List
      preselected = this.widgetData.capitalisationApproachId;
    }

    this.rows = this.getTextAndListSettingsOrSetDefault(this.textAndListSectionName, [
      {
        topicId: 1,
        fieldName: 'hardCoreTopSliceApproachWeighting',
        fieldNameMiddle: 'hardCoreTopSliceNetMarketValueRounded',
        isNumericField: true,
        isPercentage: true,
        decimals: 2,
        lastTopicId: 1,
        selected: preselected === 1
      }, {
        topicId: 2,
        fieldName: 'directCapitalisationApproachWeighting',
        fieldNameMiddle: 'directCapitalisationValue',
        isNumericField: true,
        isPercentage: true,
        decimals: 2,
        lastTopicId: 2,
        selected: preselected === 2
      }, {
        topicId: 3,
        fieldName: 'discountedCashFlowApproachWeighting',
        fieldNameMiddle: 'discountedCashFlowApproachValue',
        isNumericField: true,
        isPercentage: true,
        decimals: 2,
        lastTopicId: 3,
        selected: true
      }, {
        topicId: 4,
        fieldName: 'costApproachWeighting',
        fieldNameMiddle: 'costApproachValue',
        isNumericField: true,
        isPercentage: true,
        decimals: 2,
        lastTopicId: 4,
        selected: false
      }, {
        topicId: 5,
        fieldName: 'salesComparisonApproachWeighting',
        fieldNameMiddle: 'salesComparisonApproachValue',
        isNumericField: true,
        isPercentage: true,
        decimals: 2,
        lastTopicId: 5,
        selected: false
      }, {
        topicId: 6,
        fieldName: 'incomeCapitalizationApproachWeighting',
        fieldNameMiddle: 'incomeCapitalizationApproachValue',
        isNumericField: true,
        isPercentage: true,
        decimals: 2,
        lastTopicId: 6,
        selected: false
      }
    ]);
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Delete current row event handler
   * @param rows {any[]} Rows
   */
  public deleteCurrentRow(rows: any[]): void {
    this.setTextAndListSettings(this.textAndListSectionName, rows);

    this.deleteCurrentRowSubject.next();

    //clear weightings for deleted/not shown rows
    for (const row of rows.filter(r => !r.selected && r.fieldName !== null)) {
      this.widgetData[row.fieldName] = null;
    }
  }

  /**
   * Add row event handler
   * @param rows {any[]} Rows
   */
  public addRow(rows: any[]): void {
    const unselectedFields = this.rows.filter(item => !item.selected);
    if (unselectedFields.length > 0) {
      this.setTextAndListSettings(this.textAndListSectionName, rows);
      this.addRowSubject.next({
        topicId: this.getTopicId(),
        fieldName: null,
        isTextField: false,
        isNumericField: true,
        isDataField: false,
        lastTopicId: this.getTopicId(),
        selected: true
      });
    }
  }

  /**
   * Move current row up event handler
   * @param rows {any[]} Rows
   */
  public moveCurrentRowUp(rows: any[]): void {
    this.setTextAndListSettings(this.textAndListSectionName, rows);
    this.moveCurrentRowUpSubject.next();
  }

  /**
   * Move current row down event handler
   * @param rows {any[]} Rows
   */
  public moveCurrentRowDown(rows: any[]): void {
    this.setTextAndListSettings(this.textAndListSectionName, rows);
    this.moveCurrentRowDownSubject.next();
  }

  /**
   * Data changed event handler
   * @param rows {any[]} Rows
   */
  public onDataChanged(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.setDirtyFlag(true);
  }

  /**
   * Get topic Id
   */
  private getTopicId() {
    const unselectedFields = this.rows.filter(item => !item.selected);
    return Math.min(...unselectedFields.map(item => item.topicId));
  }

  /**
   * Layout change event handler
   * @param newLayout The new layout
   */
  public layoutChanged(newLayout: string): void {
    this.widgetConfig.widgetSettings.currentLayout = newLayout;
  }

  /**
   * Calculate Adopted Value
   */
  public calculateAdoptedValue() {
    if (this.weightingsInvalid) {
      this.widgetData.adoptedValue = 0;
    }
    else {
      this.widgetData.adoptedValue = 0;
      for (const row of this.rows) {
        if (row.fieldNameMiddle && row.fieldName) {
          try {
            const value = this.widgetData[row.fieldNameMiddle];
            const weighting = this.widgetData[row.fieldName];

            this.widgetData.adoptedValue += value * weighting / 100;
          } catch (e) {
            //Nothing
          }
        }
      }
    }
  }
}
